export type GossipCategory = {
  slug: string;
  name: string;
  short: string;
  description: string;
  icon: string;
};

export type GossipItem = {
  id: string;
  title: string;
  excerpt: string;
  cover: string;
  category: string;
  tags: string[];
  duration: string;
  views: number;
  likes: number;
  date: string;
  author: string;
  hot: boolean;
  paragraphs: string[];
};

export type Topic = {
  id: string;
  title: string;
  subtitle: string;
  cover: string;
  count: number;
  category: string;
};

export const categories: GossipCategory[] = [
  {
    slug: "first-scene",
    name: "第一现场",
    short: "第一现场",
    description: "最靠近事件发生的一刻，镜头就架在旁边，未经剪辑的现场原貌。",
    icon: "ri-live-line",
  },
  {
    slug: "real-shot",
    name: "现场实拍",
    short: "实拍",
    description: "路人视角与随手记录，还原当时真实的人群、光线与声音。",
    icon: "ri-camera-lens-line",
  },
  {
    slug: "full-version",
    name: "完整版",
    short: "完整版",
    description: "从开场到收工一条不落，把被剪掉的细节全部补回来。",
    icon: "ri-film-line",
  },
  {
    slug: "recording",
    name: "现场录像",
    short: "录像",
    description: "后台、彩排、换装间隙，长时间机位留下的珍贵素材。",
    icon: "ri-vidicon-line",
  },
  {
    slug: "sneak-peek",
    name: "路透片段",
    short: "路透",
    description: "还在筹备就已被拍到的画面，提前一睹为快。",
    icon: "ri-radar-line",
  },
  {
    slug: "fancam",
    name: "分直拍",
    short: "分直拍",
    description: "定点机位逐人跟拍，每一帧都是现场的呼吸感。",
    icon: "ri-focus-3-line",
  },
];

export const gossipItems: GossipItem[] = [
  {
    id: "1001",
    title: "音乐节后台实拍片段流出 现场氛围直接拉满",
    excerpt: "散场前的最后一段走廊画面被完整记录，灯光、音箱与人声交织在一起，隔着屏幕都能感受到那股热浪。",
    cover:
      "https://readdy.ai/api/search-image?query=Backstage%20corridor%20of%20a%20summer%20music%20festival%20at%20night%2C%20stage%20lights%20bleeding%20through%20heavy%20curtains%2C%20road%20cases%20and%20coiled%20cables%20scattered%20on%20the%20floor%2C%20atmospheric%20haze%20and%20warm%20lens%20flare%2C%20cinematic%20editorial%20photography%2C%20warm%20amber%20and%20deep%20crimson%20color%20grading%2C%20moody%20stage%20lighting%2C%20shallow%20depth%20of%20field%2C%20no%20recognizable%20faces%2C%20high%20detail%2C%20editorial%20composition&width=800&height=450&seq=gossip-1001&orientation=landscape",
    category: "real-shot",
    tags: ["音乐节", "后台", "实拍"],
    duration: "03:42",
    views: 1286000,
    likes: 34200,
    date: "2026-08-28",
    author: "瓜田影像社",
    hot: true,
    paragraphs: [
      "这段画面来自音乐节收官日的后台通道机位。镜头没有刻意运镜，只是安静地架在走廊尽头，把工作人员来回奔走、音箱低鸣、以及远处舞台传来的鼓点全部收了进来。",
      "最吸引人的是散场前最后两分钟：灯光师逐排关灯，人群的欢呼从场馆深处涌进来，整条走廊被暖红色的余光照亮，很多人说这是今年最有临场感的一段实拍。",
      "视频全长三分多钟，没有做任何加速和剪辑，保留了原始环境音，戴上耳机观看效果更佳。",
    ],
  },
  {
    id: "1002",
    title: "颁奖礼红毯第一现场 全程无剪辑记录",
    excerpt: "从第一位嘉宾踏上红毯到主持人收尾，一个机位守完全程，把镜头外的等待、整理与互动都留了下来。",
    cover:
      "https://readdy.ai/api/search-image?query=Awards%20ceremony%20red%20carpet%20entrance%20in%20the%20evening%2C%20long%20carpet%20stretching%20toward%20glowing%20venue%20lights%2C%20photographers%20silhouetted%20behind%20velvet%20ropes%2C%20warm%20gold%20spotlights%20and%20confetti%20dust%20in%20the%20air%2C%20cinematic%20editorial%20photography%2C%20warm%20amber%20and%20deep%20crimson%20color%20grading%2C%20moody%20lighting%2C%20shallow%20depth%20of%20field%2C%20no%20recognizable%20faces%2C%20high%20detail%2C%20editorial%20composition&width=800&height=450&seq=gossip-1002&orientation=landscape",
    category: "first-scene",
    tags: ["颁奖礼", "红毯", "第一现场"],
    duration: "12:08",
    views: 986000,
    likes: 27100,
    date: "2026-08-24",
    author: "瓜田影像社",
    hot: true,
    paragraphs: [
      "红毯环节的视频通常只保留几分钟精华，而这一段选择了完整记录：从空场准备、灯光校准，到嘉宾陆续入场，再到主持人收尾，全部按时间线保留。",
      "镜头位置在主入口侧后方，既能拍到整条红毯的纵深，也能捕捉到等待区里整理服装、互相打招呼的细节。正是这些镜头之外的小片刻，让整段影像显得格外真实。",
      "需要提醒的是，画面中涉及部分到场嘉宾，站内仅作现场氛围记录，不进行人物评价。",
    ],
  },
  {
    id: "1003",
    title: "剧组夜戏完整版录像 收工瞬间全记录",
    excerpt: "从布光到收工整整一夜，完整版录像把“再来一条”的耐心和收工那一刻的松口气都拍了下来。",
    cover:
      "https://readdy.ai/api/search-image?query=Film%20set%20at%20night%20with%20large%20diffusion%20frames%20and%20warm%20practical%20lanterns%2C%20light%20stands%20and%20camera%20cranes%20lined%20up%20along%20a%20quiet%20street%2C%20misty%20air%20catching%20the%20beam%20of%20a%20spotlight%2C%20cinematic%20editorial%20photography%2C%20warm%20amber%20and%20deep%20crimson%20color%20grading%2C%20moody%20lighting%2C%20shallow%20depth%20of%20field%2C%20no%20recognizable%20faces%2C%20high%20detail%2C%20editorial%20composition&width=800&height=450&seq=gossip-1003&orientation=landscape",
    category: "full-version",
    tags: ["剧组", "夜戏", "完整版"],
    duration: "26:15",
    views: 742000,
    likes: 19800,
    date: "2026-08-19",
    author: "瓜田影像社",
    hot: false,
    paragraphs: [
      "完整版录像的价值在于“不删”。这一段从傍晚布光开始，一直拍到凌晨收工，中间的等待、调试、反复拍摄都被保留下来。",
      "你能看到灯光助理一次次爬上梯子调整柔光布，也能听到导演在监视器后面说“再来一条”。当最后一声“收工”响起，整个片场同时松了一口气，这个瞬间特别打动观众。",
      "整段素材长约二十六分钟，适合想了解影视拍摄流程的观众收藏观看。",
    ],
  },
  {
    id: "1004",
    title: "演唱会前排分直拍 手抖也挡不住的热情",
    excerpt: "分直拍机位锁定舞台中央，尽管偶尔会抖，但那正是现场观众视角最诚实的记录。",
    cover:
      "https://readdy.ai/api/search-image?query=Concert%20stage%20view%20from%20the%20front%20rows%20with%20glowing%20handheld%20light%20sticks%20and%20a%20sea%20of%20raised%20arms%2C%20dramatic%20beams%20of%20amber%20and%20red%20light%20cutting%20through%20smoke%2C%20confetti%20falling%20across%20the%20frame%2C%20cinematic%20editorial%20photography%2C%20warm%20amber%20and%20deep%20crimson%20color%20grading%2C%20moody%20lighting%2C%20shallow%20depth%20of%20field%2C%20no%20recognizable%20faces%2C%20high%20detail%2C%20editorial%20composition&width=800&height=450&seq=gossip-1004&orientation=landscape",
    category: "fancam",
    tags: ["演唱会", "分直拍", "现场"],
    duration: "04:56",
    views: 1154000,
    likes: 41500,
    date: "2026-08-15",
    author: "前排记录员",
    hot: true,
    paragraphs: [
      "分直拍和官方机位最大的区别，是它带着现场观众的心跳。画面会随着节奏轻微晃动，偶尔还会被前面举起来的手挡住一角，但正因如此，它更像亲历。",
      "这一段完整保留了副歌部分的全场大合唱，灯海与声浪同时涌上来，很多人表示看到一半就起了一身鸡皮疙瘩。",
      "建议戴耳机观看，左右声道的合唱层次非常清晰。",
    ],
  },
  {
    id: "1005",
    title: "综艺录制路透片段 嘉宾互动花絮曝光",
    excerpt: "节目还在录制阶段，场外机位就先拍到了休息间隙的轻松互动，气氛比正片还松弛。",
    cover:
      "https://readdy.ai/api/search-image?query=Television%20studio%20compound%20at%20dusk%20with%20portable%20lighting%20rigs%20and%20production%20carts%2C%20warm%20glow%20spilling%20from%20open%20stage%20doors%2C%20soft%20rain%20slick%20ground%20reflecting%20amber%20lights%2C%20cinematic%20editorial%20photography%2C%20warm%20amber%20and%20deep%20crimson%20color%20grading%2C%20moody%20lighting%2C%20shallow%20depth%20of%20field%2C%20no%20recognizable%20faces%2C%20high%20detail%2C%20editorial%20composition&width=800&height=450&seq=gossip-1005&orientation=landscape",
    category: "sneak-peek",
    tags: ["综艺", "路透", "花絮"],
    duration: "02:31",
    views: 634000,
    likes: 15200,
    date: "2026-08-11",
    author: "场外观察",
    hot: false,
    paragraphs: [
      "路透的魅力在于不确定性——你看到的是节目尚未定档时的原貌，连工作人员都还没想好要怎么剪。",
      "这段片段拍到的是录制间隙，几位嘉宾走到场边喝水、聊天，气氛非常松弛自然。没有脚本，也没有打光，纯粹是生活化的一刻。",
      "因为属于录制期间的现场记录，画面清晰度有限，请以了解现场氛围为主。",
    ],
  },
  {
    id: "1006",
    title: "商场路演现场实拍 围观人群挤满三层楼",
    excerpt: "从一楼中庭到三楼围栏，实拍镜头记录下一场路演是如何把人流一点点吸过来的。",
    cover:
      "https://readdy.ai/api/search-image?query=Shopping%20mall%20atrium%20during%20a%20live%20roadshow%2C%20crowds%20gathered%20along%20the%20balconies%20of%20three%20floors%2C%20warm%20stage%20lights%20washing%20over%20a%20small%20platform%2C%20glowing%20storefronts%20in%20the%20background%2C%20cinematic%20editorial%20photography%2C%20warm%20amber%20and%20deep%20crimson%20color%20grading%2C%20moody%20lighting%2C%20shallow%20depth%20of%20field%2C%20no%20recognizable%20faces%2C%20high%20detail%2C%20editorial%20composition&width=800&height=450&seq=gossip-1006&orientation=landscape",
    category: "real-shot",
    tags: ["路演", "商场", "实拍"],
    duration: "05:20",
    views: 458000,
    likes: 9800,
    date: "2026-08-07",
    author: "路过拍一下",
    hot: false,
    paragraphs: [
      "这场路演最开始只有零星几个人围观，主持人还在暖场。随着音乐响起，一楼中庭的人越聚越多，二楼、三楼的围栏边也陆续站满了人。",
      "实拍镜头完整记录了人流聚集的过程，这个过程比舞台本身更有意思——它展示了现场氛围是如何一步步被点燃的。",
      "结尾处人群齐声鼓掌的画面，是整段实拍里最有感染力的部分。",
    ],
  },
  {
    id: "1007",
    title: "发布会第一现场 提问环节意外不断",
    excerpt: "发布会最不好剪的就是提问环节，这段第一现场把现场的真实反应完整保留了下来。",
    cover:
      "https://readdy.ai/api/search-image?query=Press%20conference%20hall%20with%20rows%20of%20seats%20and%20glowing%20accent%20lighting%20along%20the%20stage%20edge%2C%20microphones%20arranged%20on%20a%20long%20table%2C%20large%20backlit%20screen%20casting%20warm%20amber%20tones%2C%20cinematic%20editorial%20photography%2C%20warm%20amber%20and%20deep%20crimson%20color%20grading%2C%20moody%20lighting%2C%20shallow%20depth%20of%20field%2C%20no%20recognizable%20faces%2C%20high%20detail%2C%20editorial%20composition&width=800&height=450&seq=gossip-1007&orientation=landscape",
    category: "first-scene",
    tags: ["发布会", "第一现场", "提问"],
    duration: "09:44",
    views: 521000,
    likes: 11400,
    date: "2026-08-03",
    author: "瓜田影像社",
    hot: false,
    paragraphs: [
      "发布会的前半段通常是流程化的致辞，真正的变数往往出现在提问环节。这段第一现场从第一个问题开始记录，把现场的每一次停顿、笑声和追问都留了下来。",
      "最精彩的部分出现在第六分钟：一个问题引发了持续近两分钟的补充说明，台下记者的笔几乎没停过。",
      "整段视频保留了现场原声，没有添加解说，适合想了解事件原貌的观众。",
    ],
  },
  {
    id: "1008",
    title: "后台彩排完整版录像 走位细节首度公开",
    excerpt: "正式演出前的彩排往往最真实，完整版录像逐段记录了走位、灯位与换场的全部细节。",
    cover:
      "https://readdy.ai/api/search-image?query=Empty%20theatre%20stage%20during%20a%20technical%20rehearsal%2C%20rows%20of%20warm%20spotlights%20being%20aimed%20down%20at%20marked%20floor%20positions%2C%20faded%20velvet%20curtains%20and%20rigging%20ropes%2C%20dust%20motes%20drifting%20in%20the%20beams%2C%20cinematic%20editorial%20photography%2C%20warm%20amber%20and%20deep%20crimson%20color%20grading%2C%20moody%20lighting%2C%20shallow%20depth%20of%20field%2C%20no%20recognizable%20faces%2C%20high%20detail%2C%20editorial%20composition&width=800&height=450&seq=gossip-1008&orientation=landscape",
    category: "recording",
    tags: ["彩排", "后台", "录像"],
    duration: "18:30",
    views: 388000,
    likes: 7600,
    date: "2026-07-30",
    author: "幕后机位",
    hot: false,
    paragraphs: [
      "彩排录像里没有观众，也没有掌声，只有导演的口令和灯光切换的声音。正是这种“空场”状态，让每一个走位细节都格外清晰。",
      "完整版按节目顺序排列，从开场灯位到最后的换场流程，一段不落。想了解舞台调度是怎么工作的，这段素材非常直观。",
      "视频中的地面标记是走位点，配合镜头切换观看，能更清楚每位演员的移动轨迹。",
    ],
  },
  {
    id: "1009",
    title: "电影首映路透 主创同框瞬间被抓拍",
    excerpt: "首映礼入场通道的路透片段，把主创团队同框走进影厅的那一刻完整留了下来。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinema%20entrance%20in%20the%20evening%20with%20a%20glowing%20marquee%20and%20velvet%20rope%20barriers%2C%20warm%20amber%20light%20spilling%20onto%20the%20pavement%2C%20film%20posters%20lit%20in%20soft%20crimson%20tones%2C%20cinematic%20editorial%20photography%2C%20warm%20amber%20and%20deep%20crimson%20color%20grading%2C%20moody%20lighting%2C%20shallow%20depth%20of%20field%2C%20no%20recognizable%20faces%2C%20high%20detail%2C%20editorial%20composition&width=800&height=450&seq=gossip-1009&orientation=landscape",
    category: "sneak-peek",
    tags: ["首映", "路透", "电影"],
    duration: "01:58",
    views: 412000,
    likes: 10300,
    date: "2026-07-26",
    author: "场外观察",
    hot: false,
    paragraphs: [
      "首映礼的入口通道其实很窄，路透机位就守在一旁，抓拍到了主创团队依次入场并短暂停下合影的画面。",
      "片段很短，但信息量不小：走位顺序、停留时长、现场欢呼的音量变化都被保留了下来。",
      "这段属于现场记录性质，画面以氛围为主，不涉及任何作品剧情内容。",
    ],
  },
  {
    id: "1010",
    title: "话剧谢幕分直拍 观众起立鼓掌三分钟",
    excerpt: "分直拍对准舞台中央，谢幕时全场起立鼓掌，掌声一直持续到演员第三次返场。",
    cover:
      "https://readdy.ai/api/search-image?query=Theatre%20stage%20during%20a%20curtain%20call%20with%20a%20single%20warm%20spotlight%20on%20the%20center%2C%20rows%20of%20seats%20rising%20in%20shadowed%20silhouette%2C%20deep%20crimson%20curtains%20partially%20drawn%2C%20golden%20dust%20drifting%20in%20the%20beam%2C%20cinematic%20editorial%20photography%2C%20warm%20amber%20and%20deep%20crimson%20color%20grading%2C%20moody%20lighting%2C%20shallow%20depth%20of%20field%2C%20no%20recognizable%20faces%2C%20high%20detail%2C%20editorial%20composition&width=800&height=450&seq=gossip-1010&orientation=landscape",
    category: "fancam",
    tags: ["话剧", "谢幕", "分直拍"],
    duration: "06:12",
    views: 296000,
    likes: 8900,
    date: "2026-07-21",
    author: "前排记录员",
    hot: false,
    paragraphs: [
      "谢幕是剧场里最特别的时刻。分直拍固定在舞台正前方，把观众的掌声与演员的返场完整记录了下来。",
      "从第一次鞠躬到最后一次退场，掌声几乎没有停过。第三分钟时全场起立，那一刻镜头也跟着轻轻晃了一下，是拍摄者情绪最真实的反应。",
      "这段视频没有配乐，全部是现场原声，氛围还原度很高。",
    ],
  },
  {
    id: "1011",
    title: "街头快闪现场实拍记录 路人反应太真实",
    excerpt: "没有预告、没有围挡，一场街头快闪突然开始的瞬间被实拍完整记录。",
    cover:
      "https://readdy.ai/api/search-image?query=Busy%20city%20plaza%20at%20dusk%20with%20warm%20street%20lamps%20and%20glowing%20shop%20windows%2C%20a%20small%20open%20circle%20forming%20among%20passersby%2C%20long%20shadows%20across%20the%20paving%2C%20cinematic%20editorial%20photography%2C%20warm%20amber%20and%20deep%20crimson%20color%20grading%2C%20moody%20lighting%2C%20shallow%20depth%20of%20field%2C%20no%20recognizable%20faces%2C%20high%20detail%2C%20editorial%20composition&width=800&height=450&seq=gossip-1011&orientation=landscape",
    category: "real-shot",
    tags: ["快闪", "街头", "实拍"],
    duration: "03:05",
    views: 367000,
    likes: 11200,
    date: "2026-07-17",
    author: "路过拍一下",
    hot: false,
    paragraphs: [
      "快闪最打动人的地方是路人的真实反应。前三十秒大家还在正常通行，音乐响起后，人群慢慢围成一个圈，手机陆续举了起来。",
      "实拍镜头站在广场边缘，既拍到了表演的圆圈，也拍到了外围一脸惊讶、停下脚步的人。",
      "结尾处掌声响起，人群自然散开，整个过程没有任何人为的引导。",
    ],
  },
  {
    id: "1012",
    title: "颁奖礼后台第一现场 奖杯传递全程记录",
    excerpt: "舞台背后的通道才是故事发生的地方，这段第一现场记录了奖杯从后台到台前的完整路径。",
    cover:
      "https://readdy.ai/api/search-image?query=Backstage%20corridor%20of%20an%20awards%20venue%20with%20tall%20gold%20curtains%20and%20a%20display%20table%20holding%20a%20gleaming%20trophy%2C%20warm%20amber%20light%20stripes%20along%20the%20wall%2C%20soft%20crimson%20carpet%2C%20cinematic%20editorial%20photography%2C%20warm%20amber%20and%20deep%20crimson%20color%20grading%2C%20moody%20lighting%2C%20shallow%20depth%20of%20field%2C%20no%20recognizable%20faces%2C%20high%20detail%2C%20editorial%20composition&width=800&height=450&seq=gossip-1012&orientation=landscape",
    category: "first-scene",
    tags: ["颁奖礼", "后台", "第一现场"],
    duration: "07:38",
    views: 344000,
    likes: 8100,
    date: "2026-07-12",
    author: "瓜田影像社",
    hot: false,
    paragraphs: [
      "后台第一现场的魅力在于“过渡”。台上是聚光灯和掌声，台下是快步走动的脚步和低声确认流程的对话。",
      "这段镜头跟着奖杯从后台准备区一路走到侧台入口，中间的等待、交接、确认都被保留下来，构成了一个完整的时间切片。",
      "视频没有旁白，只保留环境声，观看时有很强的临场感。",
    ],
  },
  {
    id: "1013",
    title: "剧场彩排完整版 灯光调试幕后揭秘",
    excerpt: "灯光师逐排调整色温与角度的过程被完整拍下，原来一场演出的氛围是这样一点点调出来的。",
    cover:
      "https://readdy.ai/api/search-image?query=Lighting%20console%20and%20truss%20rig%20above%20an%20empty%20stage%2C%20dozens%20of%20warm%20amber%20fixtures%20in%20different%20intensities%2C%20dark%20auditorium%20below%20with%20faint%20seat%20outlines%2C%20cinematic%20editorial%20photography%2C%20warm%20amber%20and%20deep%20crimson%20color%20grading%2C%20moody%20lighting%2C%20shallow%20depth%20of%20field%2C%20no%20recognizable%20faces%2C%20high%20detail%2C%20editorial%20composition&width=800&height=450&seq=gossip-1013&orientation=landscape",
    category: "full-version",
    tags: ["剧场", "灯光", "完整版"],
    duration: "21:47",
    views: 233000,
    likes: 6400,
    date: "2026-07-08",
    author: "幕后机位",
    hot: false,
    paragraphs: [
      "同样是彩排，这一段的重点在灯。完整版从主灯位点亮开始，一步一步记录灯光师如何逐排调整角度与色温。",
      "你会在视频里看到同一个场景被反复切换五六种灯光方案，直到导演点头为止。这种“试错”的过程，正是舞台氛围最终成立的原因。",
      "整段素材超过二十分钟，适合对舞台技术感兴趣的观众慢慢看。",
    ],
  },
  {
    id: "1014",
    title: "音乐节散场路透 大巴车离开画面被拍下",
    excerpt: "散场后的停车场是最真实的收尾现场，路透镜头记录下班车依次亮灯驶离的整个过程。",
    cover:
      "https://readdy.ai/api/search-image?query=Festival%20parking%20lot%20late%20at%20night%20with%20a%20row%20of%20tour%20buses%20warming%20up%2C%20headlights%20glowing%20softly%20through%20light%20mist%2C%20empty%20field%20beyond%20with%20faint%20stage%20glow%20on%20the%20horizon%2C%20cinematic%20editorial%20photography%2C%20warm%20amber%20and%20deep%20crimson%20color%20grading%2C%20moody%20lighting%2C%20shallow%20depth%20of%20field%2C%20no%20recognizable%20faces%2C%20high%20detail%2C%20editorial%20composition&width=800&height=450&seq=gossip-1014&orientation=landscape",
    category: "sneak-peek",
    tags: ["音乐节", "散场", "路透"],
    duration: "02:44",
    views: 271000,
    likes: 7200,
    date: "2026-07-04",
    author: "场外观察",
    hot: false,
    paragraphs: [
      "散场往往是最没人拍的时段，但这段路透恰好记录下了它。安静下来的停车场、远处还在闪的舞台余光、以及一辆辆亮起车灯的大巴，构成了很有电影感的一帧。",
      "视频三分钟不到，节奏缓慢，适合在通勤路上随手点开看一看。",
      "画面里没有刻意寻找人物，只是把散场后的空气留了下来。",
    ],
  },
  {
    id: "1015",
    title: "演唱会安可分直拍 全场大合唱名场面",
    excerpt: "安可环节全场亮起手机灯，分直拍把灯海与大合唱同步记录了下来。",
    cover:
      "https://readdy.ai/api/search-image?query=Concert%20encore%20moment%20with%20thousands%20of%20phone%20lights%20glowing%20across%20a%20dark%20arena%2C%20soft%20amber%20stage%20haze%20rising%2C%20warm%20crimson%20wash%20on%20the%20backdrop%2C%20cinematic%20editorial%20photography%2C%20warm%20amber%20and%20deep%20crimson%20color%20grading%2C%20moody%20lighting%2C%20shallow%20depth%20of%20field%2C%20no%20recognizable%20faces%2C%20high%20detail%2C%20editorial%20composition&width=800&height=450&seq=gossip-1015&orientation=landscape",
    category: "fancam",
    tags: ["演唱会", "安可", "分直拍"],
    duration: "08:21",
    views: 892000,
    likes: 33800,
    date: "2026-06-29",
    author: "前排记录员",
    hot: true,
    paragraphs: [
      "安可环节是全场的情绪高点。灯光全部熄灭后，场馆里陆续亮起手机灯，几秒钟之内就汇聚成一片灯海。",
      "分直拍完整记录了这段从安静到爆发的全过程，包括全场大合唱的每一个段落。因为是定点机位，画面的稳定性比手持更佳。",
      "不少观众表示，最后两分钟的和声部分值得反复回看。",
    ],
  },
  {
    id: "1016",
    title: "跨年晚会后台录像 换装间隙全记录",
    excerpt: "后台通道与换装区的长时间录像，把晚会最紧张也最有序的一面展示了出来。",
    cover:
      "https://readdy.ai/api/search-image?query=Backstage%20wardrobe%20corridor%20of%20a%20big%20year-end%20stage%20show%2C%20racks%20of%20sequined%20costumes%20under%20warm%20bulbs%2C%20mirrors%20and%20quick-change%20screens%2C%20glitter%20on%20the%20floor%2C%20cinematic%20editorial%20photography%2C%20warm%20amber%20and%20deep%20crimson%20color%20grading%2C%20moody%20lighting%2C%20shallow%20depth%20of%20field%2C%20no%20recognizable%20faces%2C%20high%20detail%2C%20editorial%20composition&width=800&height=450&seq=gossip-1016&orientation=landscape",
    category: "recording",
    tags: ["跨年晚会", "后台", "录像"],
    duration: "15:03",
    views: 318000,
    likes: 7700,
    date: "2026-06-24",
    author: "幕后机位",
    hot: false,
    paragraphs: [
      "晚会后台的换装间隙是最考验协作的时段。录像机位固定在通道口，把一次次来回奔跑和快速补妆都记录了下来。",
      "与台上的从容不同，后台是分秒必争的。视频里能听到清晰的倒计时口令，配合脚步声，紧张感扑面而来。",
      "这段录像时长十五分钟，适合想了解大型晚会运转方式的观众。",
    ],
  },
];

export const topics: Topic[] = [
  {
    id: "topic-festival",
    title: "音乐节现场特辑",
    subtitle: "从入场彩排到散场车灯，把整个音乐节的一天串起来看",
    cover:
      "https://readdy.ai/api/search-image?query=Wide%20festival%20grounds%20at%20golden%20hour%20with%20a%20distant%20main%20stage%20silhouette%20and%20long%20shadows%20across%20the%20lawn%2C%20warm%20dust%20in%20the%20air%2C%20flags%20and%20string%20lights%20along%20the%20path%2C%20cinematic%20editorial%20photography%2C%20warm%20amber%20and%20deep%20crimson%20color%20grading%2C%20moody%20lighting%2C%20shallow%20depth%20of%20field%2C%20no%20recognizable%20faces%2C%20high%20detail%2C%20editorial%20composition&width=600&height=400&seq=gossip-topic-01&orientation=landscape",
    count: 4,
    category: "real-shot",
  },
  {
    id: "topic-awards",
    title: "颁奖季红毯合集",
    subtitle: "红毯、后台、领奖台，一条线看完颁奖礼的全部现场",
    cover:
      "https://readdy.ai/api/search-image?query=Elegant%20awards%20hall%20interior%20with%20warm%20chandeliers%20and%20a%20deep%20crimson%20carpet%20leading%20to%20a%20lit%20stage%2C%20empty%20velvet%20seats%20in%20soft%20shadow%2C%20gold%20accents%20on%20the%20walls%2C%20cinematic%20editorial%20photography%2C%20warm%20amber%20and%20deep%20crimson%20color%20grading%2C%20moody%20lighting%2C%20shallow%20depth%20of%20field%2C%20no%20recognizable%20faces%2C%20high%20detail%2C%20editorial%20composition&width=600&height=400&seq=gossip-topic-02&orientation=landscape",
    count: 3,
    category: "first-scene",
  },
  {
    id: "topic-backstage",
    title: "幕后与彩排档案",
    subtitle: "后台通道、彩排灯位、换装间隙，那些台前看不到的准备工作",
    cover:
      "https://readdy.ai/api/search-image?query=Dim%20theatre%20backstage%20with%20coiled%20cables%20and%20a%20lighting%20console%20glowing%20in%20the%20corner%2C%20warm%20amber%20work%20lights%20along%20the%20wall%2C%20faded%20velvet%20curtain%20at%20the%20edge%2C%20cinematic%20editorial%20photography%2C%20warm%20amber%20and%20deep%20crimson%20color%20grading%2C%20moody%20lighting%2C%20shallow%20depth%20of%20field%2C%20no%20recognizable%20faces%2C%20high%20detail%2C%20editorial%20composition&width=600&height=400&seq=gossip-topic-03&orientation=landscape",
    count: 3,
    category: "recording",
  },
  {
    id: "topic-live",
    title: "演唱会直拍精选",
    subtitle: "分直拍机位下的灯海、合唱与安可，全是现场最真实的声音",
    cover:
      "https://readdy.ai/api/search-image?query=Arena%20seen%20from%20high%20above%20before%20a%20show%2C%20thousands%20of%20tiny%20amber%20lights%20dotting%20the%20dark%20seating%20bowl%2C%20faint%20crimson%20haze%20over%20the%20empty%20stage%2C%20cinematic%20editorial%20photography%2C%20warm%20amber%20and%20deep%20crimson%20color%20grading%2C%20moody%20lighting%2C%20shallow%20depth%20of%20field%2C%20no%20recognizable%20faces%2C%20high%20detail%2C%20editorial%20composition&width=600&height=400&seq=gossip-topic-04&orientation=landscape",
    count: 3,
    category: "fancam",
  },
];

export const brandStats = [
  { value: "8,600+", label: "收录现场视频" },
  { value: "6", label: "现场内容分类" },
  { value: "12 万", label: "每日浏览人次" },
  { value: "96%", label: "用户回访满意度" },
];

export const testimonials = [
  {
    name: "追热点的阿凯",
    role: "娱乐资讯爱好者",
    quote:
      "最省事的一点是热度排行，每天点开就知道今天大家在聊什么，不用一个个平台翻。分类也做得很细，实拍和路透分得很清楚。",
  },
  {
    name: "咖啡馆老板·林",
    role: "长期用户",
    quote:
      "我喜欢完整版和录像这两类，能把被剪掉的细节补回来。视频详情页的相关推荐也很准，常常一看就是一小时。",
  },
  {
    name: "大学生小周",
    role: "分直拍爱好者",
    quote:
      "分直拍专区是宝藏，机位稳定、原声保留得好。页面很干净，没有乱七八糟的弹窗，加载也快。",
  },
  {
    name: "内容编辑·老陈",
    role: "从业者",
    quote:
      "后台第一现场这类素材很少见，能看出站点在筛选上是用心的。每个视频都写了内容简介，信息量比标题党强太多。",
  },
];

export const faqs = [
  {
    q: "在哪里可以找到「视频吃瓜第一现场」的内容？",
    a: "首页的「第一现场」分类入口，或顶部导航中的「第一现场」栏，都会集中展示最贴近事件发生瞬间的现场记录，并在热度排行中优先展示当日话题度最高的片段。",
  },
  {
    q: "「完整版」和「录像」有什么区别？",
    a: "完整版强调不删减、按时间线完整呈现一场活动的全过程；录像则更侧重后台、彩排、换装间隙这类长时间机位留下的片段。两者在内容分类页有独立的标签页。",
  },
  {
    q: "路透片段为什么画质有时不够清晰？",
    a: "路透通常拍摄于活动筹备或录制阶段，机位较远、现场光线不稳定，因此无法保证画质。我们保留原始画面，只做基础的色彩与音频处理，尽量还原现场氛围。",
  },
  {
    q: "站内视频可以下载或二次使用吗？",
    a: "站内内容仅用于现场氛围的记录与分享，站方不提供下载，也不支持任何形式的二次剪辑与商用。如涉及版权或肖像相关诉求，可通过联系页面告知，我们会尽快处理。",
  },
  {
    q: "分直拍和其他分类最大的不同是什么？",
    a: "分直拍采用定点机位逐人跟拍，画面稳定性更好，也保留了完整的现场原声，适合想专注看舞台调度与表演细节的观众。",
  },
  {
    q: "如何向我们推荐一段值得收录的现场视频？",
    a: "在「关于我们」页面的联系表单中填写视频来源、发生时间与简要说明即可。我们会在人工审核后决定是否收录，并通过邮件回复处理结果。",
  },
];

export const siteIntro = {
  brandName: "瓜田影像社",
  tagline: "视频吃瓜 · 第一现场影像档案",
  slogan: "把最靠近现场的那一帧，留给你",
  email: "contact@guatian-live.example.com",
};